import { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { AlertCircle, CheckCircle2, ListChecks, Loader2, X, XCircle } from 'lucide-react'

import { toast } from 'components/ui/toast-store'
import { DEFAULT_PROJECT_TYPES } from 'constants/projectTypes'
import axiosInterceptor from 'services/axiosInterceptor'
import { API_BASE_URL } from 'services/constants'
import type { AiPlanBody, AiPlanProblem, AiPlanStep, AiPlanView } from 'services/aiService'
import { aiClearPlanError, executeAiPlan, fetchAiPlan, saveAiPlan, undoAiPlan, type AiPlanActivity, type AiState } from 'slices/aiSlice'
import getCapabilities from 'utils/capabilities'

import { canUndo, confirmBlocker, gapsOf, progressOf, removalOf, replaceStep, withoutSteps } from '../plan/planSteps'
import { refreshListsAfter } from '../plan/refresh'
import { ExecuteBar } from './ExecuteBar'
import { ExecutionProgress } from './ExecutionProgress'
import { PlanStepRow } from './PlanStepRow'
import { ResultLinks } from './ResultLinks'
import { UndoButton } from './UndoButton'

/**
 * The plan card (MYD-623, MYD-624): what Ask MDT proposes to do, for the user
 * to review, correct and confirm, and then what happened.
 *
 * Before it runs, every edit is saved at once with PUT /ai/plans/:id, and the
 * server's answer (re-validated, with what is still missing) replaces the plan
 * shown. A refused edit leaves the saved plan in place and shows why.
 *
 * Confirm runs it (POST .../execute). While it runs the card polls the plan,
 * which the executor saves step by step, and shows each step finish. Then: how
 * it ended, what did not run and why, links to what it made, and Undo for 24
 * hours. The lists it changed are read again, so the screens behind the panel
 * catch up without a reload.
 */

/** How often a running plan is read again. A run is a few seconds. */
export const POLL_MS = 1000

/**
 * Project types to offer: the defaults and the account's own (LookupList).
 * Read here, not through lookupListSlice: its fetch replaces whatever lists
 * another screen has loaded.
 */
const useProjectTypes = (wanted: boolean): readonly string[] => {
	const [types, setTypes] = useState<readonly string[]>(DEFAULT_PROJECT_TYPES)
	useEffect(() => {
		if (!wanted) return
		let live = true
		axiosInterceptor(API_BASE_URL)
			.get('/lookup-lists', { params: { listType: 'project' } })
			.then(({ data }) => {
				const own = (Array.isArray(data) ? data : []).map((item: { option?: string }) => item.option).filter((option): option is string => Boolean(option))
				if (live) setTypes([...new Set([...DEFAULT_PROJECT_TYPES, ...own])])
			})
			.catch(() => {
				/* The defaults still work: a type outside them is offered as new. */
			})
		return () => {
			live = false
		}
	}, [wanted])
	return types
}

const RUN_ENDINGS: Partial<Record<AiPlanView['status'], { icon: typeof CheckCircle2; text: string; tone: string }>> = {
	done: { icon: CheckCircle2, text: 'Done. Every step ran.', tone: 'text-success' },
	partial: { icon: AlertCircle, text: 'Partly done: some steps did not run.', tone: 'text-warning' },
	failed: { icon: XCircle, text: 'Nothing was changed: the steps did not run.', tone: 'text-destructive' },
	undone: { icon: CheckCircle2, text: 'Undone.', tone: 'text-muted-foreground' },
}

const NO_ACTIVITY: AiPlanActivity = { saving: false, executing: false, error: null }

/** "2 done, 1 failed, 1 skipped": the run at a glance. */
const tally = (view: AiPlanView) => {
	const counts = { done: 0, failed: 0, skipped: 0 }
	for (const result of view.results) counts[result.status] += 1
	return (['done', 'failed', 'skipped'] as const)
		.filter(status => counts[status])
		.map(status => `${counts[status]} ${status}`)
		.join(', ')
}

const RAN = new Set(['done', 'partial', 'failed'])

export function PlanCard({ planId }: { planId: string }) {
	const dispatch = useDispatch<any>()
	const view = useSelector((state: { ai: AiState }) => state.ai.plans[planId])
	const activity = useSelector((state: { ai: AiState }) => state.ai.planActivity[planId]) ?? NO_ACTIVITY
	const user = useSelector((state: any) => state.userLogin?.userInfo)
	const capabilities = getCapabilities(user)
	const hasProject = Boolean(view?.plan.steps.some(step => step.op === 'project.create'))
	const projectTypes = useProjectTypes(hasProject)
	const running = activity.executing || view?.status === 'executing'

	const problemsByStep = useMemo(() => {
		const map = new Map<string | null, AiPlanProblem[]>()
		for (const problem of activity.error?.problems ?? []) map.set(problem.step, [...(map.get(problem.step) ?? []), problem])
		return map
	}, [activity.error])

	// Follow a run: the executor saves each step's result as it lands.
	useEffect(() => {
		if (!running) return
		const timer = setInterval(() => dispatch(fetchAiPlan(planId)), POLL_MS)
		return () => clearInterval(timer)
	}, [running, dispatch, planId])

	// Once it has run, or been undone, read the lists it changed again.
	const lastStatus = useRef(view?.status)
	useEffect(() => {
		const before = lastStatus.current
		lastStatus.current = view?.status
		if (!view || before === view.status) return
		if ((before === 'executing' && RAN.has(view.status)) || view.status === 'undone') refreshListsAfter(dispatch, view, user?._id)
	}, [view, dispatch, user?._id])

	if (!view) return null

	const refs = view.refs ?? {}
	const editable = view.status === 'proposed' || view.status === 'edited'
	const busy = activity.saving || activity.executing
	const save = (plan: AiPlanBody) => dispatch(saveAiPlan({ id: planId, plan }))
	const change = (step: AiPlanStep) => save(replaceStep(view.plan, step))

	const remove = async (step: AiPlanStep, index: number) => {
		const ids = removalOf(view.plan, step.id)
		const others = ids.length - 1
		const ok = await toast.confirm({
			title: `Remove step ${index + 1}?`,
			message: others
				? `${others === 1 ? 'One later step depends' : `${others} later steps depend`} on it and will be removed too.`
				: 'It will be left out when the plan runs.',
			confirmLabel: others ? `Remove ${ids.length} steps` : 'Remove step',
		})
		if (ok) save(withoutSteps(view.plan, ids))
	}

	const blocker = confirmBlocker({ view, capabilities, saving: activity.saving })
	const ending = RUN_ENDINGS[view.status]
	const general = [...(problemsByStep.get(null) ?? []), ...(activity.error?.problems ?? []).filter(problem => problem.step && !view.plan.steps.some(step => step.id === problem.step))]
	const progress = progressOf(view)
	const notRun = view.plan.steps.filter(step => progress[step.id] === 'failed' || progress[step.id] === 'skipped')

	return (
		<section className="mt-2 rounded-xl border border-gray-200 bg-gray-50/60 p-3" aria-label="Proposed plan">
			<header className="mb-2 flex items-start gap-2">
				<ListChecks className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
				<div className="min-w-0 flex-1">
					<h3 className="text-sm font-semibold text-gray-900">{editable ? 'Review the plan' : running ? 'Running the plan…' : 'The plan'}</h3>
					<p className="text-xs text-gray-600">{view.plan.summary}</p>
				</div>
				{(activity.saving || running) && <Loader2 className="mt-0.5 h-3.5 w-3.5 animate-spin text-muted-foreground" aria-label={running ? 'Running' : 'Saving'} />}
			</header>

			{editable ? (
				<ol className="space-y-2">
					{view.plan.steps.map((step, index) => (
						<PlanStepRow
							key={step.id}
							step={step}
							index={index}
							plan={view.plan}
							refs={refs}
							gaps={gapsOf(step.id, view)}
							problems={problemsByStep.get(step.id) ?? []}
							projectTypes={projectTypes}
							disabled={busy}
							onChange={change}
							onRemove={() => remove(step, index)}
						/>
					))}
				</ol>
			) : (
				<ExecutionProgress view={view} />
			)}

			{activity.error && (
				<div role="alert" className="mt-2 flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-2.5 py-2 text-xs text-destructive">
					<AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
					<div className="flex-1">
						<p>{activity.error.message}</p>
						{general.map((problem, n) => (
							<p key={n} className="mt-0.5">
								{problem.message}
							</p>
						))}
					</div>
					<button type="button" onClick={() => dispatch(aiClearPlanError(planId))} aria-label="Dismiss" className="rounded p-0.5 hover:bg-destructive/10">
						<X className="h-3 w-3" />
					</button>
				</div>
			)}

			{editable ? (
				<div className="mt-3">
					<ExecuteBar blocker={blocker} executing={running} stepCount={view.plan.steps.length} onConfirm={() => dispatch(executeAiPlan(planId))} />
				</div>
			) : (
				!running && (
					<div className="mt-3 space-y-3 border-t border-gray-100 pt-3">
						{ending && (
							<div role="status" className={`text-sm ${ending.tone}`}>
								<p className="flex items-center gap-1.5">
									<ending.icon className="h-4 w-4" aria-hidden="true" />
									{ending.text}
								</p>
								{view.status === 'partial' && (
									<p className="mt-1 text-xs text-gray-600">
										{tally(view)}. {notRun.length === 1 ? 'One step did not run;' : `${notRun.length} steps did not run;`} the reasons are listed above. What was made is kept.
									</p>
								)}
							</div>
						)}
						<ResultLinks view={view} />
						{canUndo(view) && (
							<div className="flex items-center justify-between gap-2">
								<p className="text-xs text-muted-foreground">You can undo this for 24 hours.</p>
								<UndoButton
									undoing={Boolean(activity.undoing)}
									disabled={!capabilities.canWrite}
									reason={capabilities.isImpersonating ? 'Support sessions are read-only.' : null}
									onUndo={() => dispatch(undoAiPlan(planId))}
								/>
							</div>
						)}
					</div>
				)
			)}
		</section>
	)
}

export default PlanCard
